import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Platform } from 'react-native';
import {
    type Edge,
    type NativeSafeAreaViewProps,
    SafeAreaView as RNSACSafeAreaView,
    useSafeAreaInsets,
} from 'react-native-safe-area-context';
import {
    isWeb,
    ScrollView as RNScrollView,
    ScrollView,
    type ScrollViewProps,
    Separator,
    type StackProps,
    View,
    type ViewProps,
    XStack,
    YStack,
} from 'tamagui';

import {
    CONTAINER_HORIZONTAL_PADDING,
    HEADING_HORIZONTAL_PADDING,
    isNonMobileWeb,
} from '../../constants';
import { borderCurve } from '../componentHelpers';

export type ContainerComponent = FC<PropsWithChildren<unknown>>;

type SafeAreaViewProps = NativeSafeAreaViewProps & {
    noBottomPadding?: boolean;
};

export const SafeAreaView = ({
    noBottomPadding = false,
    children,
    style,
    ...props
}: SafeAreaViewProps) => {
    const insets = useSafeAreaInsets();

    return (
        <RNSACSafeAreaView
            style={[
                {
                    paddingBottom:
                        insets.bottom > 0 || isNonMobileWeb || noBottomPadding
                            ? 0
                            : 20, // handles phones with no bottom inset (like iPhone SE)
                },
                style ?? {
                    flex: 1,
                },
            ]}
            {...props}
        >
            {children}
        </RNSACSafeAreaView>
    );
};

export const PageBelowHeader = ({ children, ...viewProps }: ViewProps) => {
    const shadow = Platform.select({
        ios: {
            shadowColor: 'rgba(0, 0, 0, 0.05)',
            shadowOffset: {
                width: 0,
                height: -3,
            },
            shadowOpacity: 0.05,
            shadowRadius: 6,
        },
        android: {
            elevation: 6,
        },
        web: {
            boxShadow: '0px -10px 10px rgba(0, 0, 0, 0.02)',
        },
    });

    return (
        <YStack
            backgroundColor="$white"
            borderTopLeftRadius={32}
            borderTopRightRadius={32}
            padding={20}
            height="100%"
            width="100%"
            marginHorizontal="auto"
            $sm={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
            $lg={{
                borderTopLeftRadius: 32,
                borderTopRightRadius: 32,
                padding: 40,
            }}
            style={{ ...shadow }}
            {...borderCurve('continuous')}
            {...viewProps}
        >
            {children}
        </YStack>
    );
};

interface SmoothieScrollViewProps extends ScrollViewProps {
    containerStyle?: StackProps;
}

// RN doesn't like when you try and re-export ScrollView apparently
export const SmoothieScrollView = ({
    children,
    style,
    showsVerticalScrollIndicator = isWeb,
    containerStyle,
    ...props
}: SmoothieScrollViewProps) => (
    <RNScrollView
        showsVerticalScrollIndicator={showsVerticalScrollIndicator}
        // @ts-expect-error: 'auto' is valid value for web
        overflow={isWeb ? 'auto' : 'visible'}
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
        {...props}
    >
        <View
            marginHorizontal={16}
            $platform-web={{
                marginTop: 20,
                paddingBottom: 20,
            }}
            $sm={{ alignSelf: 'center' }}
            {...containerStyle}
        >
            {children}
        </View>
    </RNScrollView>
);

interface FullScreenContainerProps {
    children: ReactNode;
    header: ReactNode;
    footer?: ReactNode;
    separator?: boolean;
    scrollDisabled?: boolean;
    edges?: Edge[];
}

export const FullScreenContainer = ({
    children,
    header,
    footer,
    separator,
    scrollDisabled,
    edges = ['top', 'right', 'bottom', 'left'],
}: FullScreenContainerProps) => {
    const insets = useSafeAreaInsets();

    return (
        <View
            style={{
                paddingTop: edges.includes('top') ? insets.top : undefined,
                paddingBottom: edges.includes('bottom')
                    ? insets.bottom
                    : undefined,
                paddingLeft: edges.includes('left') ? insets.left : undefined,
                paddingRight: edges.includes('right')
                    ? insets.right
                    : undefined,
            }}
            flex={1}
        >
            <YStack
                height="100%"
                width="100%"
                maxWidth={isWeb ? 700 : '100%'}
                paddingBottom={isWeb ? 12 : 'unset'}
                marginHorizontal="auto"
                justifyContent="space-between"
                alignSelf="center"
            >
                <XStack
                    paddingHorizontal={HEADING_HORIZONTAL_PADDING}
                    paddingTop={12}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {header}
                </XStack>
                <YStack
                    paddingHorizontal={CONTAINER_HORIZONTAL_PADDING}
                    flex={1}
                >
                    {scrollDisabled ? (
                        children
                    ) : (
                        <ScrollView
                            showsVerticalScrollIndicator={isWeb}
                            alwaysBounceVertical={false}
                        >
                            {children}
                        </ScrollView>
                    )}
                </YStack>
                {separator && <Separator />}
                {footer !== undefined && (
                    <YStack
                        paddingHorizontal={CONTAINER_HORIZONTAL_PADDING}
                        paddingTop={16}
                    >
                        {footer}
                    </YStack>
                )}
            </YStack>
        </View>
    );
};

export enum InsetContainerVariant {
    LIGHT = 'light',
    DARK = 'dark',
}

interface InsetContainerProps extends StackProps {
    variant?: InsetContainerVariant;
    children: ReactNode;
}

export const InsetContainer = ({
    variant = InsetContainerVariant.LIGHT,
    children,
    ...props
}: InsetContainerProps) => {
    return variant === InsetContainerVariant.LIGHT ? (
        <YStack
            borderRadius={16}
            borderWidth={1}
            borderColor="$gray300"
            overflow="hidden"
            {...props}
        >
            {children}
        </YStack>
    ) : (
        <YStack
            backgroundColor="$gray200"
            borderRadius={24}
            overflow="hidden"
            {...props}
        >
            {children}
        </YStack>
    );
};
